import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from './api';
import Spinner from './Spinner';
import CustomAlert from './CustomAlert';
import ExcelJS from 'exceljs';
import {
    normalizePartNo,
    computeSequentialEcd,
    referenceManHours,
    formatDate as formatIsoDate,
} from './ltdEcdUtils';
import { useSiteNames } from './useSiteCatalog';
import { assignLtdCannibalizations } from './ltdCannibalization';
import { rankLtdAircraft } from './ltdPriority';

const ALL_SITES = 'ALL';

const EDD_STATUS = {
    EXPIRED: 'EDD Expired',
    DUE_30: 'Due within 30 Days',
    DUE_60: 'Due within 60 Days',
    DUE_90: 'Due within 90 Days',
    DUE_90_PLUS: 'Due 90+ Days',
    NO_EDD: 'No EDD Provided',
};

const EDD_STATUS_COLORS = {
    [EDD_STATUS.EXPIRED]:     { bg: '#dc3545', fg: '#ffffff' },
    [EDD_STATUS.DUE_30]:      { bg: '#28a745', fg: '#ffffff' },
    [EDD_STATUS.DUE_60]:      { bg: '#17a2b8', fg: '#ffffff' },
    [EDD_STATUS.DUE_90]:      { bg: '#ffc107', fg: '#212529' },
    [EDD_STATUS.DUE_90_PLUS]: { bg: '#fd7e14', fg: '#ffffff' },
    [EDD_STATUS.NO_EDD]:      { bg: '#6c757d', fg: '#ffffff' },
};

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    const colors = {
        bg: isDark ? '#121212' : '#f4f7f6',
        cardBg: isDark ? '#1e1e1e' : '#fff',
        text: isDark ? '#e0e0e0' : '#333',
        subtleText: isDark ? '#a0a0a0' : '#666',
        border: isDark ? '#444' : '#ddd',
        shadow: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)',
        link: isDark ? '#6cb2f7' : '#007bff',
        headerBg: isDark ? '#2a2a2a' : '#e9eef3',
        rowAlt: isDark ? '#262626' : '#fafbfc',
    };

    return {
        page: {
            backgroundColor: colors.bg,
            minHeight: '100vh',
            padding: '20px',
            color: colors.text,
        },
        headerCard: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
        },
        navLink: {
            color: colors.link,
            textDecoration: 'none',
            fontSize: '14px',
            display: 'inline-block',
            marginBottom: '10px',
        },
        title: {
            fontSize: '24px',
            fontWeight: 'bold',
            color: colors.text,
            margin: '0 0 6px 0',
        },
        subtitle: {
            fontSize: '14px',
            color: colors.subtleText,
            margin: 0,
        },
        section: {
            backgroundColor: colors.cardBg,
            borderRadius: '8px',
            boxShadow: `0 2px 5px ${colors.shadow}`,
            border: `1px solid ${colors.border}`,
            overflow: 'hidden',
            marginBottom: '20px',
        },
        sectionHeader: {
            padding: '15px 20px',
            borderBottom: `2px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: colors.headerBg,
        },
        sectionTitle: {
            fontSize: '18px',
            fontWeight: 'bold',
            color: colors.text,
            margin: 0,
        },
        controls: {
            display: 'flex',
            gap: '12px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            flexWrap: 'wrap',
            alignItems: 'center',
        },
        input: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
            minWidth: '220px',
        },
        select: {
            padding: '8px 12px',
            borderRadius: '4px',
            border: `1px solid ${colors.border}`,
            backgroundColor: isDark ? '#333' : '#fff',
            color: colors.text,
        },
        exportButton: {
            padding: '8px 16px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
        },
        tableWrap: { overflowX: 'auto' },
        scrollableTableWrap: {
            overflowX: 'auto',
            overflowY: 'auto',
            maxHeight: '560px',
        },
        table: { width: '100%', borderCollapse: 'collapse' },
        th: {
            position: 'sticky',
            top: 0,
            padding: '12px 10px',
            textAlign: 'left',
            fontSize: '12px',
            fontWeight: 'bold',
            color: colors.text,
            backgroundColor: colors.headerBg,
            borderBottom: `2px solid ${colors.border}`,
            cursor: 'pointer',
            userSelect: 'none',
            whiteSpace: 'nowrap',
        },
        thNum: { textAlign: 'right' },
        td: {
            padding: '8px 10px',
            fontSize: '12px',
            color: colors.text,
            borderBottom: `1px solid ${colors.border}`,
            verticalAlign: 'top',
        },
        tdNum: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
        rowAlt: { backgroundColor: colors.rowAlt },
        loadingBox: { padding: '40px', textAlign: 'center' },
        empty: { padding: '40px', textAlign: 'center', color: colors.subtleText },
        sortIcon: { marginLeft: '4px', fontSize: '10px' },
        statusBadge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: 'bold',
            whiteSpace: 'nowrap',
        },
        statsRow: {
            display: 'flex',
            gap: '16px',
            padding: '12px 20px',
            borderBottom: `1px solid ${colors.border}`,
            flexWrap: 'wrap',
        },
        statChip: {
            backgroundColor: colors.headerBg,
            border: `1px solid ${colors.border}`,
            borderRadius: '6px',
            padding: '8px 14px',
            minWidth: '120px',
        },
        statLabel: { fontSize: '11px', color: colors.subtleText, marginBottom: '2px' },
        statValue: { fontSize: '18px', fontWeight: 'bold' },
    };
};

const parseDateOnly = (s) => {
    if (!s) return null;
    const str = String(s).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str);
    if (!m) {
        const d = new Date(str);
        return Number.isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }
    return new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
};

const todayLocal = () => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
};

const formatDisplayDate = (d) => {
    if (!d) return '';
    const date = d instanceof Date ? d : parseDateOnly(d);
    if (!date) return '';
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const getEddStatus = (edd, today) => {
    if (!edd) return EDD_STATUS.NO_EDD;
    const ms = edd.getTime() - today.getTime();
    const daysOut = Math.round(ms / 86400000);
    if (daysOut < 0) return EDD_STATUS.EXPIRED;
    if (daysOut <= 30) return EDD_STATUS.DUE_30;
    if (daysOut <= 60) return EDD_STATUS.DUE_60;
    if (daysOut <= 90) return EDD_STATUS.DUE_90;
    return EDD_STATUS.DUE_90_PLUS;
};

const getLfdColor = (days) => {
    if (days == null || days < 0) return '#6c757d';
    if (days < 30) return '#d97a2e';
    return '#b53a3a';
};

const formatHours = (value) => (
    value == null || !Number.isFinite(Number(value)) ? '—' : Number(value).toFixed(2)
);

const onAircraftLabel = (crdStatus) => {
    if (crdStatus === 'installed') return 'Yes';
    if (crdStatus === 'not_installed') return 'No';
    return 'CRD missing';
};

const hoursAreMissing = (part) => {
    if (part.crdStatus === 'installed') return part.removeHours == null || part.installHours == null;
    if (part.crdStatus === 'not_installed') return part.installHours == null;
    return false;
};

const extractList = (responseData) => {
    if (Array.isArray(responseData)) return responseData;
    if (responseData && Array.isArray(responseData.results)) return responseData.results;
    return [];
};

const describeFetchError = (err, label) => {
    const status = err?.response?.status;
    if (status === 404) {
        return `${label}: endpoint not found (backend may need redeploy).`;
    }
    if (status === 500) {
        return `${label}: server error (database migration may be pending).`;
    }
    if (!err?.response) {
        return `${label}: network error — check your connection or try again.`;
    }
    return `${label}: request failed (${status || 'unknown'}).`;
};

function LTDAircraftPage({ theme, embedded = false }) {
    const styles = getStyles(theme);
    const siteNames = useSiteNames();
    const siteOptions = [ALL_SITES, ...siteNames];
    const [siteFilter, setSiteFilter] = useState(ALL_SITES);
    const [rawData, setRawData] = useState([]);
    const [manHoursData, setManHoursData] = useState([]);
    const [installedAircraft, setInstalledAircraft] = useState([]);
    const [fleetAircraft, setFleetAircraft] = useState([]);
    const [crdRows, setCrdRows] = useState([]);
    const [lastFlyByBuno, setLastFlyByBuno] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('focusOrder');
    const [sortDirection, setSortDirection] = useState('asc');
    const [detailsSearch, setDetailsSearch] = useState('');
    const [detailsSortField, setDetailsSortField] = useState('seq');
    const [detailsSortDirection, setDetailsSortDirection] = useState('asc');
    const [alert, setAlert] = useState({ show: false, message: '', type: '' });
    const [fetchWarnings, setFetchWarnings] = useState([]);
    const [exporting, setExporting] = useState(false);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setFetchWarnings([]);

        const params = new URLSearchParams();
        if (siteFilter && siteFilter !== ALL_SITES) {
            params.append('site_name', siteFilter);
        }
        const qs = params.toString();
        const nmcsUrl = qs ? `/nmcs-pmcs/?${qs}` : '/nmcs-pmcs/';
        const crdUrl = qs ? `/crd-all/installed-pns/?${qs}` : '/crd-all/installed-pns/';

        const crdAllUrl = qs ? `/crd-all/?${qs}` : '/crd-all/';

        const [nmcsResult, statusResult, mhResult, crdResult, fleetResult, crdAllResult] = await Promise.allSettled([
            api.get(nmcsUrl),
            api.get('/aircraft-status/'),
            api.get('/ak0-part-man-hours/'),
            api.get(crdUrl),
            api.get('/aircraft/'),
            api.get(crdAllUrl),
        ]);

        const warnings = [];
        let nmcs = [];
        let statuses = [];
        let mh = [];
        let installed = [];
        let fleet = [];
        let crdAll = [];

        if (nmcsResult.status === 'fulfilled') {
            nmcs = extractList(nmcsResult.value.data);
        } else {
            console.error('LTD: NMCS PMCS fetch failed:', nmcsResult.reason);
            warnings.push(describeFetchError(nmcsResult.reason, 'NMCS PMCS data'));
        }

        if (statusResult.status === 'fulfilled') {
            statuses = extractList(statusResult.value.data);
        } else {
            console.error('LTD: aircraft status fetch failed:', statusResult.reason);
            warnings.push(describeFetchError(statusResult.reason, 'Aircraft status'));
        }

        if (mhResult.status === 'fulfilled') {
            mh = extractList(mhResult.value.data);
        } else {
            console.error('LTD: AK0 man-hours fetch failed:', mhResult.reason);
            warnings.push(describeFetchError(mhResult.reason, 'AK0 man-hours'));
            warnings.push('ECD values will show as Unknown until man-hours data is available.');
        }

        if (crdResult.status === 'fulfilled') {
            const payload = crdResult.value.data;
            installed = Array.isArray(payload?.aircraft) ? payload.aircraft : [];
        } else {
            console.error('LTD: CRD installed parts fetch failed:', crdResult.reason);
            warnings.push(describeFetchError(crdResult.reason, 'CRD installed parts'));
            warnings.push('ECD values will show as Unknown until CRD shows which parts are still installed.');
        }

        if (fleetResult.status === 'fulfilled') {
            fleet = extractList(fleetResult.value.data);
        } else {
            console.error('LTD: fleet fetch failed:', fleetResult.reason);
            warnings.push(describeFetchError(fleetResult.reason, 'Fleet aircraft'));
            warnings.push('Cannibalization donors will be blank until the fleet list is available.');
        }

        if (crdAllResult.status === 'fulfilled') {
            crdAll = extractList(crdAllResult.value.data);
        } else {
            console.error('LTD: CRD All fetch failed:', crdAllResult.reason);
            warnings.push(describeFetchError(crdAllResult.reason, 'CRD All'));
        }

        const lfdMap = {};
        for (const s of statuses) {
            const buno = (s.buno || '').toString().trim();
            if (buno && s.last_fly_date) lfdMap[buno] = s.last_fly_date;
        }

        setRawData(nmcs);
        setLastFlyByBuno(lfdMap);
        setManHoursData(mh);
        setInstalledAircraft(installed);
        setFleetAircraft(fleet);
        setCrdRows(crdAll);
        setFetchWarnings(warnings);

        if (warnings.length > 0) {
            setAlert({
                show: true,
                message: warnings.join(' '),
                type: nmcs.length === 0 ? 'error' : 'warning',
            });
        }

        setLoading(false);
    }, [siteFilter]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const manHoursByPart = useMemo(() => {
        const map = new Map();
        for (const row of manHoursData) {
            const key = normalizePartNo(row.part_no);
            if (!key) continue;
            map.set(key, referenceManHours(row));
        }
        return map;
    }, [manHoursData]);

    const crdIndex = useMemo(() => {
        const map = new Map();
        for (const aircraft of installedAircraft) {
            const site = (aircraft.site_name || '').trim().toUpperCase();
            const buno = (aircraft.buno || '').toString().trim();
            if (!buno) continue;
            const key = `${site}::${buno}`;
            const parts = map.get(key) || new Set();
            for (const partNo of aircraft.part_numbers || []) {
                const normalized = normalizePartNo(partNo);
                if (normalized) parts.add(normalized);
            }
            map.set(key, parts);
        }
        return map;
    }, [installedAircraft]);

    const { summaryRows, detailsRows, computeError } = useMemo(() => {
        try {
        const today = todayLocal();
        const ak0Rows = (rawData || []).filter(r =>
            (r.proj || '').toString().trim().toUpperCase() === 'AK0'
        );

        const aircraftMap = new Map();

        for (const row of ak0Rows) {
            const partNo = (row.part_no || '').toString().trim();
            if (!partNo) continue;

            const modex = (row.modex || '').toString().trim();
            const site = (row.site_name || '').toString().trim();
            const serno = (row.serno || '').toString().trim();
            const buno = serno || modex;
            if (!buno) continue;

            const aircraftKey = `${site || '__UNKNOWN__'}::${buno}`;
            let agg = aircraftMap.get(aircraftKey);
            if (!agg) {
                agg = {
                    site,
                    buno,
                    modex,
                    parts: new Map(),
                };
                aircraftMap.set(aircraftKey, agg);
            }

            const normPart = normalizePartNo(partNo);
            let part = agg.parts.get(normPart);
            if (!part) {
                part = {
                    partNo,
                    nomen: (row.nomen || '').toString().trim(),
                    edd: null,
                };
                agg.parts.set(normPart, part);
            } else if (!part.nomen && row.nomen) {
                part.nomen = row.nomen.toString().trim();
            }

            const edd = parseDateOnly(row.est_delv_dt);
            if (edd && (!part.edd || edd > part.edd)) {
                part.edd = edd;
            }
        }

        const summary = [];
        const details = [];
        const ltdAircraft = [];

        for (const agg of aircraftMap.values()) {
            if (agg.parts.size < 2) continue;

            const lfdRaw = lastFlyByBuno[agg.buno] || lastFlyByBuno[agg.modex] || null;
            const lfd = parseDateOnly(lfdRaw);
            const daysDown = lfd
                ? Math.max(0, Math.floor((today.getTime() - lfd.getTime()) / 86400000))
                : null;

            if (daysDown == null || daysDown < 30) continue;

            const installedSet = crdIndex.get(`${(agg.site || '').trim().toUpperCase()}::${agg.buno}`);
            const partsInput = Array.from(agg.parts.values()).map(p => {
                const hours = manHoursByPart.get(normalizePartNo(p.partNo));
                const normPart = normalizePartNo(p.partNo);
                let crdStatus = 'missing';
                if (installedSet) {
                    crdStatus = installedSet.has(normPart) ? 'installed' : 'not_installed';
                }
                return {
                    partNo: p.partNo,
                    nomen: p.nomen,
                    edd: p.edd,
                    removeHours: hours ? hours.removeHours : null,
                    installHours: hours ? hours.installHours : null,
                    crdStatus,
                };
            });

            let scheduledParts;
            let finalEcd;
            let hasUnknownEcd;
            try {
                ({ parts: scheduledParts, finalEcd, hasUnknownEcd } = computeSequentialEcd(partsInput, today));
            } catch (ecdErr) {
                console.error(`LTD ECD error for ${agg.buno}:`, ecdErr);
                scheduledParts = partsInput.map(p => ({
                    ...p,
                    totalHours: null,
                    workDays: null,
                    partEcd: null,
                }));
                finalEcd = null;
                hasUnknownEcd = true;
            }

            const aircraftKey = `${agg.site}::${agg.buno}`;
            ltdAircraft.push({
                aircraftKey,
                site: agg.site,
                buno: agg.buno,
                parts: partsInput,
            });

            summary.push({
                aircraftKey: `${agg.site}::${agg.buno}`,
                site: agg.site,
                buno: agg.buno,
                ak0Count: agg.parts.size,
                daysDown,
                partHours: scheduledParts.map(p => p.totalHours),
                lfd,
                lfdStr: formatDisplayDate(lfd),
                finalEcd,
                finalEcdStr: hasUnknownEcd ? 'Unknown' : formatDisplayDate(finalEcd),
                finalEcdSortKey: hasUnknownEcd || !finalEcd ? Number.MAX_SAFE_INTEGER : finalEcd.getTime(),
                hasUnknownEcd,
                missingManHours: scheduledParts.filter(hoursAreMissing).length,
                crdMissing: scheduledParts.some(p => p.crdStatus === 'missing'),
                cannibalizeEcd: null,
                cannibalizeEcdStr: '—',
                cannibalizeEcdSortKey: Number.MAX_SAFE_INTEGER,
                cannibalizeDaysSaved: null,
            });

            scheduledParts.forEach((p, idx) => {
                const eddStatus = getEddStatus(p.edd, today);
                details.push({
                    id: `${agg.site}::${agg.buno}::${normalizePartNo(p.partNo)}`,
                    aircraftKey: `${agg.site}::${agg.buno}`,
                    site: agg.site,
                    buno: agg.buno,
                    seq: idx + 1,
                    partNo: p.partNo,
                    nomen: p.nomen,
                    edd: p.edd,
                    eddStr: formatDisplayDate(p.edd),
                    eddStatus,
                    onAircraft: onAircraftLabel(p.crdStatus),
                    removeHours: p.removeHours,
                    removeHoursStr: formatHours(p.removeHours),
                    installHours: p.installHours,
                    installHoursStr: formatHours(p.installHours),
                    totalHours: p.totalHours,
                    totalHoursStr: formatHours(p.totalHours),
                    workDays: p.workDays,
                    partEcd: p.partEcd,
                    partEcdStr: p.partEcd ? formatDisplayDate(p.partEcd) : '—',
                    daysDown,
                    hoursMissing: hoursAreMissing(p),
                    donorBuno: '',
                    donorVerified: null,
                    donorAk0Count: null,
                    donorLifeStr: '',
                    partDaysSaved: null,
                    aircraftDaysSaved: null,
                });
            });
        }

        let assignments = { byPartKey: new Map(), byAircraftKey: new Map() };
        try {
            assignments = assignLtdCannibalizations({
                ltdAircraft,
                ak0Rows,
                fleetAircraft,
                crdRows,
                lastFlyByBuno,
                today,
                siteFilter,
            });
        } catch (assignErr) {
            console.error('LTD cannibalization assignment error:', assignErr);
        }

        for (const row of summary) {
            const joint = assignments.byAircraftKey.get(row.aircraftKey);
            if (!joint) continue;
            row.cannibalizeEcd = joint.cannibalizeEcd;
            row.cannibalizeHasUnknown = joint.cannibalizeHasUnknown;
            row.cannibalizeEcdStr = joint.cannibalizeHasUnknown
                ? 'Unknown'
                : (joint.cannibalizeEcd ? formatDisplayDate(joint.cannibalizeEcd) : '—');
            row.cannibalizeEcdSortKey = joint.cannibalizeHasUnknown || !joint.cannibalizeEcd
                ? Number.MAX_SAFE_INTEGER
                : joint.cannibalizeEcd.getTime();
            row.cannibalizeDaysSaved = joint.aircraftDaysSaved;
        }

        const rankedSummary = rankLtdAircraft(summary, today);

        for (const row of details) {
            const pull = assignments.byPartKey.get(`${row.site}::${row.buno}::${normalizePartNo(row.partNo)}`);
            if (!pull) continue;
            row.donorBuno = pull.donorBuno;
            row.donorVerified = pull.verified;
            row.donorAk0Count = pull.donorAk0Count;
            row.donorLifeStr = pull.donorLifeStr;
            row.partDaysSaved = pull.partDaysSaved;
            row.aircraftDaysSaved = pull.aircraftDaysSaved;
        }

        return { summaryRows: rankedSummary, detailsRows: details, computeError: '' };
        } catch (err) {
            console.error('LTD aircraft compute error:', err);
            return { summaryRows: [], detailsRows: [], computeError: err?.message || 'Failed to process LTD aircraft data.' };
        }
    }, [rawData, lastFlyByBuno, manHoursByPart, crdIndex, fleetAircraft, crdRows, siteFilter]);

    useEffect(() => {
        if (computeError) {
            setAlert({ show: true, message: computeError, type: 'error' });
        }
    }, [computeError]);

    const filteredSummary = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return summaryRows;
        return summaryRows.filter(r =>
            String(r.buno || '').toLowerCase().includes(term) ||
            String(r.site || '').toLowerCase().includes(term)
        );
    }, [summaryRows, searchTerm]);

    const sortedSummary = useMemo(() => {
        const copy = [...filteredSummary];
        const dir = sortDirection === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            let av = a[sortField];
            let bv = b[sortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return copy;
    }, [filteredSummary, sortField, sortDirection]);

    const filteredDetails = useMemo(() => {
        const term = detailsSearch.trim().toLowerCase();
        if (!term) return detailsRows;
        return detailsRows.filter(r => {
            const hay = `${r.buno} ${r.partNo} ${r.nomen} ${r.site}`.toLowerCase();
            return hay.includes(term);
        });
    }, [detailsRows, detailsSearch]);

    const sortedDetails = useMemo(() => {
        const copy = [...filteredDetails];
        const dir = detailsSortDirection === 'asc' ? 1 : -1;
        copy.sort((a, b) => {
            let av = a[detailsSortField];
            let bv = b[detailsSortField];
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            if (av < bv) return -1 * dir;
            if (av > bv) return 1 * dir;
            return 0;
        });
        return copy;
    }, [filteredDetails, detailsSortField, detailsSortDirection]);

    const headlineStats = useMemo(() => {
        const missingHoursParts = detailsRows.filter(r => r.hoursMissing).length;
        const unknownEcd = summaryRows.filter(r => r.hasUnknownEcd).length;
        return {
            ltdMultiAk0: summaryRows.length,
            manHourPartsLoaded: manHoursData.length,
            missingManHours: missingHoursParts,
            unknownEcd,
        };
    }, [summaryRows, detailsRows, manHoursData.length]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            const ascendingFirst = field === 'buno' || field === 'site'
                || field === 'focusOrder' || field === 'daysToUp' || field === 'manHoursToUp';
            setSortDirection(ascendingFirst ? 'asc' : 'desc');
        }
    };

    const sortIcon = (field) => {
        if (sortField !== field) return null;
        return <span style={styles.sortIcon}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
    };

    const handleDetailsSort = (field) => {
        if (detailsSortField === field) {
            setDetailsSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setDetailsSortField(field);
            setDetailsSortDirection('asc');
        }
    };

    const detailsSortIcon = (field) => {
        if (detailsSortField !== field) return null;
        return <span style={styles.sortIcon}>{detailsSortDirection === 'asc' ? '▲' : '▼'}</span>;
    };

    const handleSummaryBunoClick = (buno, site) => {
        const filterVal = siteFilter === ALL_SITES ? `${site} ${buno}` : buno;
        setDetailsSearch(filterVal.trim());
        setTimeout(() => {
            const el = document.getElementById('ltd-details-section');
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 0);
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const workbook = new ExcelJS.Workbook();
            const suffix = siteFilter === ALL_SITES ? 'All Sites' : siteFilter;

            const summarySheet = workbook.addWorksheet('LTD Summary');
            summarySheet.addRow([`Long Term Down Aircraft - ${suffix}`]);
            summarySheet.mergeCells(1, 1, 1, siteFilter === ALL_SITES ? 10 : 9);
            summarySheet.getCell('A1').font = { size: 14, bold: true };
            summarySheet.addRow([]);

            const summaryHeaders = siteFilter === ALL_SITES
                ? ['Site', 'BUNO', 'Rank', 'Days to UP', 'Man-hours', '# AK0 Parts', 'Days Down', 'Final ECD', 'Cannibalize ECD', 'Days Saved']
                : ['BUNO', 'Rank', 'Days to UP', 'Man-hours', '# AK0 Parts', 'Days Down', 'Final ECD', 'Cannibalize ECD', 'Days Saved'];
            summarySheet.addRow(summaryHeaders).font = { bold: true };

            sortedSummary.forEach(r => {
                const saved = r.cannibalizeDaysSaved ?? '';
                const rank = r.rank ?? '';
                const daysToUp = r.daysToUp ?? '';
                const manHours = r.manHoursToUp ?? '';
                if (siteFilter === ALL_SITES) {
                    summarySheet.addRow([
                        r.site, r.buno, rank, daysToUp, manHours, r.ak0Count, r.daysDown, r.finalEcdStr, r.cannibalizeEcdStr, saved,
                    ]);
                } else {
                    summarySheet.addRow([
                        r.buno, rank, daysToUp, manHours, r.ak0Count, r.daysDown, r.finalEcdStr, r.cannibalizeEcdStr, saved,
                    ]);
                }
            });

            const detailsSheet = workbook.addWorksheet('LTD Details');
            detailsSheet.addRow([`LTD Aircraft Details - ${suffix}`]);
            detailsSheet.mergeCells(1, 1, 1, 18);
            detailsSheet.getCell('A1').font = { size: 14, bold: true };
            detailsSheet.addRow([]);
            detailsSheet.addRow([
                'Site', 'BUNO', 'Seq', 'Part Number', 'Nomenclature', 'EDD',
                'On Aircraft', 'Remove MH', 'Install MH', 'Total MH', 'Work Days',
                'Part ECD', 'Donor', 'Donor AK0', 'Part Days Saved', 'Aircraft Days Saved',
                'EDD Status', 'Days Down',
            ]).font = { bold: true };

            sortedDetails.forEach(r => {
                detailsSheet.addRow([
                    r.site, r.buno, r.seq, r.partNo, r.nomen, r.eddStr,
                    r.onAircraft, r.removeHours ?? '', r.installHours ?? '', r.totalHours ?? '',
                    r.workDays ?? '', r.partEcdStr,
                    r.donorBuno ? (r.donorVerified === false ? `${r.donorBuno} (unverified)` : r.donorBuno) : '',
                    r.donorAk0Count ?? '',
                    r.partDaysSaved ?? '', r.aircraftDaysSaved ?? '', r.eddStatus, r.daysDown,
                ]);
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `LTD_Aircraft_${siteFilter === ALL_SITES ? 'AllSites' : siteFilter}_${formatIsoDate(new Date())}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setAlert({ show: true, message: 'Excel file exported successfully.', type: 'success' });
        } catch (err) {
            console.error('LTD export error:', err);
            setAlert({ show: true, message: 'Failed to export Excel file.', type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    return (
        <div style={embedded ? undefined : styles.page}>
            <CustomAlert {...alert} onClose={() => setAlert({ show: false })} theme={theme} />

            {!loading && fetchWarnings.length > 0 && (
                <div style={{
                    marginBottom: '16px',
                    padding: '12px 16px',
                    borderRadius: '6px',
                    backgroundColor: theme === 'dark' ? '#3a2a1a' : '#fff3cd',
                    border: `1px solid ${theme === 'dark' ? '#665500' : '#ffc107'}`,
                    color: theme === 'dark' ? '#ffd966' : '#856404',
                    fontSize: '13px',
                }}>
                    {fetchWarnings.map((msg, i) => (
                        <div key={i}>{msg}</div>
                    ))}
                </div>
            )}

            {!embedded && (
                <div style={styles.headerCard}>
                    <Link to="/" style={styles.navLink}>&larr; Back to Home</Link>
                    <h1 style={styles.title}>Long Term Down Aircraft</h1>
                    <p style={styles.subtitle}>
                        Aircraft down 30+ days since last flight with multiple AK0 supply parts.
                        Removal hours are included when CRD shows the part is still installed and can start before it arrives.
                        Install hours always apply and start the business day after EDD.
                        A donor pull makes the part available now; days saved are business days versus waiting on that EDD.
                        Rank is the soonest fully planned UP date, then fewest man-hours. Incomplete aircraft are listed after the ranked set.
                    </p>
                </div>
            )}

            <div style={styles.section}>
                <div style={styles.sectionHeader}>
                    <div>
                        <h2 style={styles.sectionTitle}>LTD Aircraft Summary</h2>
                        <p style={{ ...styles.subtitle, marginTop: '4px' }}>
                            Removal hours are included when CRD shows the part is still installed and can start before it arrives.
                            Install hours always apply and start the business day after EDD.
                            A donor pull makes the part available now; days saved are business days versus waiting on that EDD.
                            Rank is the soonest fully planned UP date, then fewest man-hours. Incomplete aircraft are listed after the ranked set.
                        </p>
                    </div>
                    <button
                        onClick={handleExport}
                        disabled={exporting || loading || sortedSummary.length === 0}
                        style={{
                            ...styles.exportButton,
                            opacity: (exporting || loading || sortedSummary.length === 0) ? 0.6 : 1,
                            cursor: (exporting || loading || sortedSummary.length === 0) ? 'not-allowed' : 'pointer',
                        }}
                    >
                        {exporting ? 'Exporting...' : 'Export to Excel'}
                    </button>
                </div>

                <div style={styles.statsRow}>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>LTD w/ Multiple AK0</div>
                        <div style={styles.statValue}>{headlineStats.ltdMultiAk0}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Man-Hour Parts Loaded</div>
                        <div style={styles.statValue}>{headlineStats.manHourPartsLoaded}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Parts Missing Man-Hours</div>
                        <div style={styles.statValue}>{headlineStats.missingManHours}</div>
                    </div>
                    <div style={styles.statChip}>
                        <div style={styles.statLabel}>Aircraft w/ Unknown ECD</div>
                        <div style={styles.statValue}>{headlineStats.unknownEcd}</div>
                    </div>
                </div>

                <div style={styles.controls}>
                    <select
                        value={siteFilter}
                        onChange={(e) => setSiteFilter(e.target.value)}
                        style={styles.select}
                    >
                        {siteOptions.map(s => (
                            <option key={s} value={s}>{s === ALL_SITES ? 'All Sites' : s}</option>
                        ))}
                    </select>
                    <input
                        type="text"
                        placeholder="Search by BUNO or Site..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={styles.input}
                    />
                    <span style={{ fontSize: '12px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                        {sortedSummary.length} aircraft
                    </span>
                </div>

                {loading ? (
                    <div style={styles.loadingBox}><Spinner /></div>
                ) : sortedSummary.length === 0 ? (
                    <div style={styles.empty}>
                        No LTD aircraft (30+ days, multiple AK0 parts) found for {siteFilter === ALL_SITES ? 'any site' : siteFilter}.
                    </div>
                ) : (
                    <div style={styles.scrollableTableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    {siteFilter === ALL_SITES && (
                                        <th style={styles.th} onClick={() => handleSort('site')}>
                                            Site{sortIcon('site')}
                                        </th>
                                    )}
                                    <th style={styles.th} onClick={() => handleSort('buno')}>
                                        BUNO{sortIcon('buno')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('focusOrder')}>
                                        Rank{sortIcon('focusOrder')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('daysToUp')}>
                                        Days to UP{sortIcon('daysToUp')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('manHoursToUp')}>
                                        Man-hours{sortIcon('manHoursToUp')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('ak0Count')}>
                                        # AK0 Parts{sortIcon('ak0Count')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('daysDown')}>
                                        Days Down{sortIcon('daysDown')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleSort('finalEcdSortKey')}>
                                        Final ECD{sortIcon('finalEcdSortKey')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleSort('cannibalizeEcdSortKey')}>
                                        Cannibalize ECD{sortIcon('cannibalizeEcdSortKey')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleSort('cannibalizeDaysSaved')}>
                                        Days Saved{sortIcon('cannibalizeDaysSaved')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedSummary.map((r, idx) => (
                                    <tr key={r.aircraftKey} style={idx % 2 === 1 ? styles.rowAlt : null}>
                                        {siteFilter === ALL_SITES && (
                                            <td style={styles.td}>{r.site}</td>
                                        )}
                                        <td style={styles.td}>
                                            <button
                                                onClick={() => handleSummaryBunoClick(r.buno, r.site)}
                                                title="Click to filter Details for this aircraft"
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    color: theme === 'dark' ? '#6cb2f7' : '#007bff',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    font: 'inherit',
                                                }}
                                            >
                                                {r.buno}
                                            </button>
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum, fontWeight: 'bold' }}>
                                            {r.rank == null ? '—' : r.rank}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.daysToUp == null ? '—' : r.daysToUp}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {formatHours(r.manHoursToUp)}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>{r.ak0Count}</td>
                                        <td style={{ ...styles.td, ...styles.tdNum, fontWeight: 'bold', color: getLfdColor(r.daysDown) }}>
                                            {r.daysDown}
                                        </td>
                                        <td style={styles.td}>
                                            {r.finalEcdStr}
                                            {r.hasUnknownEcd && r.missingManHours > 0 && (
                                                <span style={{ display: 'block', fontSize: '10px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                                                    {r.missingManHours} part(s) missing man-hours
                                                </span>
                                            )}
                                            {r.crdMissing && (
                                                <span style={{ display: 'block', fontSize: '10px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                                                    CRD missing for this BUNO
                                                </span>
                                            )}
                                        </td>
                                        <td style={styles.td}>{r.cannibalizeEcdStr}</td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.cannibalizeDaysSaved == null ? '—' : r.cannibalizeDaysSaved}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div id="ltd-details-section" style={styles.section}>
                <div style={styles.sectionHeader}>
                    <h2 style={styles.sectionTitle}>LTD Aircraft Details</h2>
                    <span style={{ fontSize: '12px', color: theme === 'dark' ? '#a0a0a0' : '#666' }}>
                        {sortedDetails.length} of {detailsRows.length} parts
                    </span>
                </div>

                <div style={styles.controls}>
                    <input
                        type="text"
                        placeholder="Search BUNO, Part Number, Nomenclature..."
                        value={detailsSearch}
                        onChange={(e) => setDetailsSearch(e.target.value)}
                        style={styles.input}
                    />
                    {detailsSearch && (
                        <button
                            onClick={() => setDetailsSearch('')}
                            style={{
                                padding: '6px 12px',
                                background: 'transparent',
                                color: theme === 'dark' ? '#6cb2f7' : '#007bff',
                                border: `1px solid ${theme === 'dark' ? '#6cb2f7' : '#007bff'}`,
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px',
                            }}
                        >
                            Clear Filter
                        </button>
                    )}
                </div>

                {loading ? (
                    <div style={styles.loadingBox}><Spinner /></div>
                ) : detailsRows.length === 0 ? (
                    <div style={styles.empty}>No LTD aircraft detail rows to display.</div>
                ) : sortedDetails.length === 0 ? (
                    <div style={styles.empty}>No records match the current filter.</div>
                ) : (
                    <div style={styles.tableWrap}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    {siteFilter === ALL_SITES && (
                                        <th style={styles.th} onClick={() => handleDetailsSort('site')}>
                                            Site{detailsSortIcon('site')}
                                        </th>
                                    )}
                                    <th style={styles.th} onClick={() => handleDetailsSort('buno')}>
                                        BUNO{detailsSortIcon('buno')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('seq')}>
                                        Seq{detailsSortIcon('seq')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('partNo')}>
                                        Part Number{detailsSortIcon('partNo')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('nomen')}>
                                        Nomenclature{detailsSortIcon('nomen')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('edd')}>
                                        EDD{detailsSortIcon('edd')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('onAircraft')}>
                                        On Aircraft{detailsSortIcon('onAircraft')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('removeHours')}>
                                        Remove MH{detailsSortIcon('removeHours')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('installHours')}>
                                        Install MH{detailsSortIcon('installHours')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('totalHours')}>
                                        Total MH{detailsSortIcon('totalHours')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('workDays')}>
                                        Work Days{detailsSortIcon('workDays')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('partEcd')}>
                                        Part ECD{detailsSortIcon('partEcd')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('donorBuno')}>
                                        Donor{detailsSortIcon('donorBuno')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('donorAk0Count')}>
                                        Donor AK0{detailsSortIcon('donorAk0Count')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('partDaysSaved')}>
                                        Part Days Saved{detailsSortIcon('partDaysSaved')}
                                    </th>
                                    <th style={{ ...styles.th, ...styles.thNum }} onClick={() => handleDetailsSort('aircraftDaysSaved')}>
                                        Aircraft Days Saved{detailsSortIcon('aircraftDaysSaved')}
                                    </th>
                                    <th style={styles.th} onClick={() => handleDetailsSort('eddStatus')}>
                                        EDD Status{detailsSortIcon('eddStatus')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedDetails.map((r, idx) => (
                                    <tr key={r.id} style={idx % 2 === 1 ? styles.rowAlt : null}>
                                        {siteFilter === ALL_SITES && (
                                            <td style={styles.td}>{r.site}</td>
                                        )}
                                        <td style={styles.td}>{r.buno}</td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>{r.seq}</td>
                                        <td style={styles.td}>{r.partNo}</td>
                                        <td style={styles.td}>{r.nomen}</td>
                                        <td style={styles.td}>{r.eddStr}</td>
                                        <td style={styles.td}>{r.onAircraft}</td>
                                        <td style={{
                                            ...styles.td,
                                            ...styles.tdNum,
                                            color: r.onAircraft === 'Yes' && r.removeHours == null ? '#dc3545' : undefined,
                                            fontWeight: r.onAircraft === 'Yes' && r.removeHours == null ? 'bold' : 'normal',
                                        }}>
                                            {r.removeHoursStr}
                                        </td>
                                        <td style={{
                                            ...styles.td,
                                            ...styles.tdNum,
                                            color: r.installHours == null ? '#dc3545' : undefined,
                                            fontWeight: r.installHours == null ? 'bold' : 'normal',
                                        }}>
                                            {r.installHoursStr}
                                        </td>
                                        <td style={{
                                            ...styles.td,
                                            ...styles.tdNum,
                                            color: r.totalHours == null ? '#dc3545' : undefined,
                                            fontWeight: r.totalHours == null ? 'bold' : 'normal',
                                        }}>
                                            {r.totalHoursStr}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.workDays == null ? '—' : r.workDays}
                                        </td>
                                        <td style={styles.td}>{r.partEcdStr}</td>
                                        <td style={styles.td} title={r.donorBuno ? (r.donorLifeStr || 'No CRD life data') : undefined}>
                                            {r.donorBuno
                                                ? (r.donorVerified === false ? `${r.donorBuno} (unverified)` : r.donorBuno)
                                                : '—'}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.donorAk0Count == null ? '—' : r.donorAk0Count}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.partDaysSaved == null ? '—' : r.partDaysSaved}
                                        </td>
                                        <td style={{ ...styles.td, ...styles.tdNum }}>
                                            {r.aircraftDaysSaved == null ? '—' : r.aircraftDaysSaved}
                                        </td>
                                        <td style={{ ...styles.td, textAlign: 'center' }}>
                                            {(() => {
                                                const sc = EDD_STATUS_COLORS[r.eddStatus] || EDD_STATUS_COLORS[EDD_STATUS.NO_EDD];
                                                return (
                                                    <span style={{
                                                        ...styles.statusBadge,
                                                        backgroundColor: sc.bg,
                                                        color: sc.fg,
                                                    }}>
                                                        {r.eddStatus}
                                                    </span>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}

export default LTDAircraftPage;
